import { partition } from "lodash-es";
import { FC, useState } from "react";

import { AddCourseModal } from "../AddCourseModal";

import { LoadingScreen } from "components/LoadingScreen";
import { EditableField } from "components/hover-widgets/EditableField";
import { AdminCourseListPanel } from "components/materials/home/admin/content/AdminCourseList";
import { CourseCodeOptionsMenu } from "components/menus/CourseCodeOptionsMenu";
import { CourseCodeType } from "components/server-types";
import { t } from "i18n/i18n";
import { AccordionTitle } from "mds/components/AccordionTitle";
import { Banner } from "mds/components/Banner";
import { Button } from "mds/components/Button";
import { IconText } from "mds/components/IconText";
import { showConfirmationModal } from "mds/components/Modal";
import { AddPlusIcon, ChevronRightIcon, FolderIcon, TextBooksIcon } from "mds/icons";
import { useAppDispatch, useAppSelector } from "store/index";
import { selectCourseCodeGroupingById, selectSortedTermCoursesByCourseCode } from "store/selectors";
import { localStateActions } from "store/slices/local";
import { useListSelector } from "store/store-hooks";
import { onTitleChanged } from "utils/store-utils";

type CourseDetailTermViewProps = {
  courseCode: CourseCodeType;
  eventPrefix?: string;
  canEdit: boolean;
};

export const CourseDetailTermView: FC<CourseDetailTermViewProps> = ({
  courseCode,
  eventPrefix,
  canEdit,
}) => {
  const dispatch = useAppDispatch();
  const [isEditing, setIsEditing] = useState(false);
  const termCourses = useListSelector((s) =>
    selectSortedTermCoursesByCourseCode(s, courseCode?.id),
  );
  const visibleTermCourses = termCourses.filter((term) => term.courses.length > 0);
  const [archivedTermCourses, activeTermCourses] = partition(
    visibleTermCourses,
    (term) => term.archived_at,
  );
  const grouping = useAppSelector((s) => selectCourseCodeGroupingById(s, courseCode.grouping_id));

  const handleEnableEditMode = () => {
    dispatch(localStateActions.setIsEditMode(true));
  };

  if (!courseCode) {
    return <LoadingScreen>{t("home.settings.no_courses")}</LoadingScreen>;
  }

  const showAddCourseModal = () => {
    showConfirmationModal({
      title: t("home.add_course_title"),
      hideCancelConfirmButtons: true,
      // eslint-disable-next-line react/no-unstable-nested-components
      children: (onClose) => (
        <AddCourseModal
          defaultCourseCodeId={courseCode.id}
          eventName={eventPrefix ? `${eventPrefix}Add Course` : undefined}
          onClose={onClose}
        />
      ),
    });
  };

  return (
    <div className="course-detail-page px-7 py-3">
      <h2 className="mb-3 flex items-center justify-between">
        <div className="flex flex-wrap items-center gap-2">
          {grouping && (
            <div className="flex items-center gap-2">
              <FolderIcon className="mb-[3px] text-blue" size="l" />
              <span className="text-black-tint-20">{grouping.title}</span>
              <ChevronRightIcon />
            </div>
          )}

          <div className="flex items-center gap-2">
            <TextBooksIcon className="mt-[1px] text-blue" size="l" />

            <EditableField
              isEditing={isEditing}
              label={t("glossary.course_code_title")}
              textSize="h2"
              value={courseCode.title}
              onSetIsEditing={setIsEditing}
              onValueChanged={(newTitle) => onTitleChanged("course_codes", courseCode.id, newTitle)}
            />
          </div>
        </div>

        {canEdit && (
          <div className="flex items-center gap-2">
            <CourseCodeOptionsMenu
              courseCode={courseCode}
              eventPrefix={eventPrefix}
              kind="gray"
              onRename={() => setIsEditing(true)}
            />

            <Button
              eventName={eventPrefix ? `${eventPrefix}Add Course` : undefined}
              kind="gray"
              size="s"
              onClick={showAddCourseModal}
            >
              <IconText iconStart={<AddPlusIcon />} text={t("common.add.course")} />
            </Button>
          </div>
        )}
      </h2>

      {!visibleTermCourses.length ? (
        <Banner
          buttonChildren={
            canEdit ? (
              <Button
                eventName={eventPrefix ? `${eventPrefix}Add Course` : undefined}
                kind="blue"
                size="s"
                onClick={showAddCourseModal}
              >
                <IconText iconStart={<AddPlusIcon />} text={t("common.add.course")} />
              </Button>
            ) : (
              <Button
                eventName={eventPrefix ? `${eventPrefix}Enable Edit Mode` : undefined}
                kind="blue"
                size="s"
                onClick={handleEnableEditMode}
              >
                {t("warning.enable_edit_mode")}
              </Button>
            )
          }
          kind="info"
        >
          {canEdit
            ? t("home.course_codes.empty_course_code_banner_text")
            : t("home.course_codes.empty_course_code_banner_text_no_edit")}
        </Banner>
      ) : (
        <>
          <ul>
            {activeTermCourses.map((term) => (
              <li className="course-panel ml-2" key={term.id}>
                <AdminCourseListPanel
                  canEdit={canEdit}
                  courses={term.courses}
                  eventPrefix={eventPrefix}
                  eventProperties={{ courseCode: courseCode.title, courseTerm: term.title }}
                  title={term.title}
                />
              </li>
            ))}
          </ul>

          {archivedTermCourses.length > 0 && (
            <AccordionTitle
              headerClassName="ml-2 w-[calc(100%-12px)] justify-between border-b border-black-tint-90 pr-4"
              iconDirection="end"
              title={t("home.terms.archived_terms")}
            >
              <ul>
                {archivedTermCourses.map((term) => (
                  <li className="course-panel ml-2" key={term.id}>
                    <AdminCourseListPanel
                      canEdit={canEdit}
                      courses={term.courses}
                      eventPrefix={eventPrefix}
                      eventProperties={{ courseCode: courseCode.title, courseTerm: term.title }}
                      title={term.title}
                      isDisabled
                    />
                  </li>
                ))}
              </ul>
            </AccordionTitle>
          )}
        </>
      )}
    </div>
  );
};
